import React, { useState, useEffect } from 'react';
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaTelegram,
  FaTimes,
} from 'react-icons/fa';
import styled from 'styled-components';
import {
  SidebarContainer,
  SocialList,
  SocialLink,
} from './styles/SocialSidebarStyles';

interface DrawerProps {
  $isOpen: boolean;
}

const Overlay = styled.div<DrawerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  z-index: 998;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileDrawer = styled.div<DrawerProps>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%)
    ${({ $isOpen }) => ($isOpen ? 'scale(1)' : 'scale(0.9)')};
  background-color: ${({ theme }) => theme.colors.background};
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 400px;
  border-radius: 12px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  z-index: 999;
  backdrop-filter: blur(10px);
  max-height: 80vh;
  overflow-y: auto;

  @media (min-width: 768px) {
    display: none;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  direction: rtl;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawerItem = styled.a`
  display: flex;
  align-items: center;
  padding: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  border-radius: 12px;
  margin: 8px 0;
  direction: rtl;
  background-color: ${({ theme }) => `${theme.colors.hover}10`};
  transition: all 0.2s ease;

  &:active {
    background-color: ${({ theme }) => `${theme.colors.hover}40`};
    transform: scale(0.98);
  }
`;

interface FacebookDropdownProps {
  $isOpen: boolean;
}

const FacebookDropdown = styled.div<FacebookDropdownProps>`
  position: absolute;
  left: calc(100% + 5px);
  top: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: 12px;
  padding: 8px;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translate(0, -50%)' : 'translate(-10px, -50%)'};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  width: 240px;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);

  @media (max-width: 767px) {
    display: none;
  }

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-style: solid;
    border-width: 8px;
    border-color: transparent ${({ theme }) => theme.colors.background}
      transparent transparent;
  }
`;

const DropdownItem = styled.a`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  border-radius: 8px;
  direction: rtl;
  text-align: right;
  margin: 4px 0;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.hover}40`};
    color: ${({ theme }) => theme.colors.text.primary};
    transform: translateX(-4px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const FacebookContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:after {
    content: '';
    position: absolute;
    left: 100%;
    top: 0;
    width: 20px;
    height: 100%;
  }
`;

const ComingSoonBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: bold;
  white-space: nowrap;
  opacity: 0.9;
`;

const InstagramWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const FacebookIconContainer = styled.div`
  background-color: #1877f2; /* Facebook blue */
  border-radius: 50%; /* Make it round */
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white; /* Icon color */
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2); /* Add shadow by default */
  font-size: 1.5rem; /* Added explicit font size for the icon */

  &:hover {
    transform: scale(1.1) translateX(2px); /* Added slight movement to the right */
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3); /* Stronger shadow on hover */
  }

  @media (max-width: 768px) {
    &:hover {
      transform: translateY(-5px) scale(1.1);
    }
  }
`;

const SocialSidebar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleFacebookClick = () => {
    if (isMobile) {
      setIsDrawerOpen(true);
    } else if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const facebookGroups = [
    {
      name: 'תאילנד ללא גבולות',
      url: 'https://www.facebook.com/groups/1081003356297706',
    },
    {
      name: 'וייטנאם ללא גבולות',
      url: 'https://www.facebook.com/groups/1257792028757276',
    },
    {
      name: 'קמבודיה ללא גבולות',
      url: 'https://www.facebook.com/groups/2969067643256568',
    },
    {
      name: 'לטייל עם חיים במזרח',
      url: 'https://www.facebook.com/profile.php?id=100095780042246',
    },
  ];

  return (
    <>
      <Overlay $isOpen={isDrawerOpen} onClick={closeDrawer} />
      <MobileDrawer $isOpen={isDrawerOpen}>
        <DrawerHeader>
          <DrawerTitle>קבוצות פייסבוק</DrawerTitle>
          <CloseButton onClick={closeDrawer}>
            <FaTimes />
          </CloseButton>
        </DrawerHeader>
        {facebookGroups.map((group, index) => (
          <DrawerItem
            key={index}
            href={group.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {group.name}
          </DrawerItem>
        ))}
      </MobileDrawer>

      <SidebarContainer>
        <SocialList>
          <FacebookContainer
            onMouseEnter={() => !isMobile && setIsDropdownOpen(true)}
            onMouseLeave={() => !isMobile && setIsDropdownOpen(false)}
          >
            <FacebookIconContainer
              onClick={handleFacebookClick}
              aria-label="Facebook"
            >
              <FaFacebookF />
            </FacebookIconContainer>
            <FacebookDropdown $isOpen={isDropdownOpen}>
              {facebookGroups.map((group, index) => (
                <DropdownItem
                  key={index}
                  href={group.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {group.name}
                </DropdownItem>
              ))}
            </FacebookDropdown>
          </FacebookContainer>
          {/* <InstagramWrapper>
            <SocialLink
              as="div"
              style={{ cursor: 'default', opacity: 0.5 }}
              aria-label="Instagram"
            >
              <FaInstagram />
              <ComingSoonBadge>בקרוב</ComingSoonBadge>
            </SocialLink>
          </InstagramWrapper> */}
          {/* <InstagramWrapper>
            <SocialLink
              as="div"
              style={{ cursor: 'default', opacity: 0.5 }}
              aria-label="WhatsApp"
            >
              <FaWhatsapp />
              <ComingSoonBadge>בקרוב</ComingSoonBadge>
            </SocialLink>
          </InstagramWrapper>
          <InstagramWrapper>
            <SocialLink
              as="div"
              style={{ cursor: 'default', opacity: 0.5 }}
              aria-label="Telegram"
            >
              <FaTelegram />
              <ComingSoonBadge>בקרוב</ComingSoonBadge>
            </SocialLink>
          </InstagramWrapper> */}
        </SocialList>
      </SidebarContainer>
    </>
  );
};

export default SocialSidebar;
